// src/components/admin/SocialMediaCampaignsEditor.jsx
// Social Media Campaigns — records a campaign entry in Firestore for tracking/conversion.
// No messages are actually sent by BespokeBeauty; this is purely a planning & tracking tool.

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  limit
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { getCustomerTargetingPreview, getSalonServices, getSalonStaff } from '../../api/salonApi';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import { useSalonLocations } from '../../hooks/useSalonLocations';

const DEFAULT_TARGETING_CRITERIA = {
  birthMonths: [],
  postalCodes: [],
  lastTransactionMonths: 0,
  lastTransactionUnit: 'months',
  transactionCount: 0,
  transactionType: 'not_selected',
  transactionAmountType: 'not_selected',
  transactionAmount: 0,
  transactionLocationId: 'not_selected',
  memberTiers: [],
  pointsType: 'not_selected',
  pointsAmount: 0,
  specificCustomers: [],
  notesSearchText: '',
  notesMatchType: 'contains_all',
  appointmentTimeValue: 0,
  appointmentTimeUnit: 'days',
  appointmentService: 'not_selected',
  appointmentStaff: 'not_selected',
  appointmentStatus: []
};

const MEMBER_TIER_OPTIONS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];
const BIRTH_MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function SocialMediaCampaignsEditor({ salonId, onUpdate }) {
  const { currentUser } = useSalonAuth();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;

  const [activeSubTab, setActiveSubTab] = useState('create-campaign');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);

  const [targetingCriteriaSaved, setTargetingCriteriaSaved] = useState(false);

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const [formData, setFormData] = useState({
    campaignName: '',
    messages: [''],
    schedulingType: 'one-time',
    scheduledDate: '',
    scheduledTime: '',
    conversionPeriod: 30,
    recurring: {
      frequency: 'daily',
      dayOfWeek: '',
      dayOfMonth: 0,
      monthOfYear: '',
      dayOfYear: 0,
      startDate: '',
      endDate: '',
      endType: 'date',
      numberOfCycles: 0,
      excludePreviousRecipients: false,
      exclusionType: 'cycles',
      excludePastCycles: 0,
      excludeBeforeDate: ''
    },
    targetingType: 'not_selected',
    targetingCriteria: { ...DEFAULT_TARGETING_CRITERIA }
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Load services and staff for appointment targeting
  useEffect(() => {
    const load = async () => {
      if (!salonId) return;
      try {
        const [services, staff] = await Promise.all([
          getSalonServices(salonId),
          getSalonStaff(salonId)
        ]);
        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
      } catch (err) {
        console.error('Error loading services/staff:', err);
      }
    };
    load();
  }, [salonId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Message management ───────────────────────────────────────────────────────

  const handleMessageChange = (index, value) => {
    setFormData(prev => {
      const msgs = [...prev.messages];
      msgs[index] = value;
      return { ...prev, messages: msgs };
    });
  };

  const addMessage = () => {
    setFormData(prev => ({ ...prev, messages: [...prev.messages, ''] }));
  };

  const removeMessage = (index) => {
    setFormData(prev => {
      const msgs = prev.messages.filter((_, i) => i !== index);
      return { ...prev, messages: msgs.length > 0 ? msgs : [''] };
    });
  };

  // ── Targeting ────────────────────────────────────────────────────────────────

  const handleInputChange = (field, value) => {
    if (field === 'targetingType') {
      setTargetingCriteriaSaved(false);
      setFormData(prev => ({
        ...prev,
        targetingType: value,
        targetingCriteria: { ...DEFAULT_TARGETING_CRITERIA }
      }));
      setSelectedCustomers([]);
      return;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleTargetingCriteriaChange = (field, value) => {
    setTargetingCriteriaSaved(false);
    setFormData(prev => ({
      ...prev,
      targetingCriteria: { ...prev.targetingCriteria, [field]: value }
    }));
  };

  const handleRecurringChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      recurring: { ...prev.recurring, [field]: value }
    }));
  };

  const handleBirthMonthsChange = (e) => {
    handleTargetingCriteriaChange('birthMonths', Array.from(e.target.selectedOptions, o => o.value));
  };

  const handleMemberTiersChange = (e) => {
    handleTargetingCriteriaChange('memberTiers', Array.from(e.target.selectedOptions, o => o.value));
  };

  const handlePostalCodesChange = (e) => {
    handleTargetingCriteriaChange('postalCodes', e.target.value.split(',').map(c => c.trim()).filter(Boolean));
  };

  const handleSaveTargetingCriteria = () => {
    setTargetingCriteriaSaved(true);
    setSuccess('Targeting criteria saved.');
    setTimeout(() => setSuccess(''), 3000);
  };

  // ── Customer search ──────────────────────────────────────────────────────────

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) { setCustomerSearchResults([]); return; }
    setSearchingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const searchLower = searchTerm.toLowerCase();
      const [emailSnap, nameSnap] = await Promise.all([
        getDocs(query(customersRef, where('salonId', '==', salonId), where('email', '>=', searchLower), where('email', '<=', searchLower + ''), limit(20))),
        getDocs(query(customersRef, where('salonId', '==', salonId), where('displayName', '>=', searchTerm), where('displayName', '<=', searchTerm + ''), limit(20)))
      ]);
      const map = new Map();
      const add = (snap) => snap.forEach(d => {
        if (!map.has(d.id)) map.set(d.id, { id: d.id, ...d.data() });
      });
      add(emailSnap); add(nameSnap);
      setCustomerSearchResults([...map.values()].filter(c => !selectedCustomers.some(s => s.id === c.id)));
    } catch (err) {
      console.error('Customer search error:', err);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchQuery(value);
    setShowCustomerDropdown(true);
    setTimeout(() => searchCustomers(value), 300);
  };

  const handleCustomerSelect = (customer) => {
    const updated = [...selectedCustomers, customer];
    setSelectedCustomers(updated);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    handleTargetingCriteriaChange('specificCustomers', updated);
  };

  const handleCustomerRemove = (id) => {
    const updated = selectedCustomers.filter(c => c.id !== id);
    setSelectedCustomers(updated);
    handleTargetingCriteriaChange('specificCustomers', updated);
  };

  // ── Validation ───────────────────────────────────────────────────────────────

  const validate = () => {
    const errors = {};
    if (!formData.campaignName.trim()) errors.campaignName = 'Campaign name is required';
    const nonEmptyMessages = formData.messages.filter(m => m.trim());
    if (nonEmptyMessages.length === 0) errors.messages = 'At least one message is required';
    if (!formData.scheduledTime) errors.scheduledTime = 'Scheduled time is required';
    if (formData.schedulingType === 'one-time') {
      if (!formData.scheduledDate) errors.scheduledDate = 'Scheduled date is required';
      if (formData.scheduledDate && formData.scheduledTime) {
        if (new Date(`${formData.scheduledDate}T${formData.scheduledTime}`) <= new Date()) {
          errors.scheduledDateTime = 'Scheduled date and time must be in the future';
        }
      }
    } else {
      if (!formData.recurring.startDate) errors.recurringStartDate = 'Start date is required';
      if (formData.recurring.endType === 'date' && !formData.recurring.endDate) errors.recurringEndDate = 'End date is required';
      if (formData.recurring.endType === 'cycles' && formData.recurring.numberOfCycles < 1) errors.numberOfCycles = 'Number of cycles must be at least 1';
    }
    if (formData.targetingType === 'not_selected') errors.targetingType = 'Target audience is required';
    if (formData.targetingType === 'specific_customer' && formData.targetingCriteria.specificCustomers.length === 0) {
      errors.specificCustomers = 'Please select at least one customer';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Schedule (record in Firestore) ───────────────────────────────────────────

  const handleSchedule = async () => {
    if (!validate()) {
      const msgs = Object.values(fieldErrors).filter(Boolean);
      if (msgs.length > 0) alert(`Please fix the following:\n\n• ${msgs.join('\n• ')}`);
      return;
    }

    try {
      setSaving(true);
      setError('');

      const scheduledFor = formData.schedulingType === 'one-time'
        ? new Date(`${formData.scheduledDate}T${formData.scheduledTime}`)
        : new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);

      const record = {
        type: 'social_media',
        platforms: ['Meta', 'Instagram', 'TikTok'],
        messageCategory: 'promotional',
        campaignName: formData.campaignName.trim(),
        messages: formData.messages.filter(m => m.trim()),
        schedulingType: formData.schedulingType,
        scheduledFor,
        scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
        scheduledTime: formData.scheduledTime,
        recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
        conversionPeriod: Number(formData.conversionPeriod) || 30,
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria,
        status: 'scheduled',
        salonId,
        createdAt: new Date(),
        createdBy: currentUser?.uid || '',
        createdByEmail: currentUser?.email || '',
        createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
        sentAt: null,
        successCount: 0,
        failureCount: 0
      };

      await addDoc(collection(db, 'socialMediaCampaigns'), record);

      setSuccess(
        formData.schedulingType === 'one-time'
          ? `Social media campaign "${formData.campaignName}" recorded for ${formData.scheduledDate} at ${formData.scheduledTime}. Conversion tracking will be active for ${formData.conversionPeriod} days.`
          : `Recurring social media campaign "${formData.campaignName}" recorded starting ${formData.recurring.startDate} at ${formData.scheduledTime}.`
      );
      setTimeout(() => setSuccess(''), 7000);

      if (onUpdate) onUpdate();
      resetForm();
    } catch (err) {
      console.error('Error recording social media campaign:', err);
      setError('Failed to record campaign: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      campaignName: '',
      messages: [''],
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: 'not_selected',
      targetingCriteria: { ...DEFAULT_TARGETING_CRITERIA }
    });
    setFieldErrors({});
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="notifications-editor">
      <h2>Social Media Campaigns</h2>

      <div className="whatsapp-info-banner" style={{ background: 'linear-gradient(135deg, #f3e5f5 0%, #e8eaf6 100%)', borderLeft: '4px solid #7c4dff' }}>
        <div className="info-icon">📱</div>
        <div className="info-content">
          <strong>Tracking-Only Channel — Meta, Instagram &amp; TikTok</strong>
          <p>
            Clicking <strong>Schedule Campaign</strong> records the campaign in the system and activates conversion tracking.
            BespokeBeauty does not publish or send anything on your behalf — run your ads through each platform's own tools and let BespokeBeauty measure the results.
          </p>
        </div>
      </div>

      {/* Sub-tab navigation */}
      <div className="sub-tab-navigation">
        <button
          className={`sub-tab-button ${activeSubTab === 'campaign-frequency' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('campaign-frequency')}
        >
          Campaign Frequency
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'create-campaign' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('create-campaign')}
        >
          Create Campaign
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Campaign Frequency tab */}
      {activeSubTab === 'campaign-frequency' && (
        <CampaignFrequencyControl salonId={salonId} campaignType="social_media" />
      )}

      {/* Create Campaign tab */}
      {activeSubTab === 'create-campaign' && (
        <div className="notification-form">
          <h3>Create Social Media Campaign</h3>

          {/* 1. Campaign Name */}
          <div className="form-section">
            <h4>1. Campaign Name</h4>
            <div className="form-group">
              <label htmlFor="smCampaignName">
                Campaign Name<span className="required-asterisk">*</span>
              </label>
              <input
                type="text"
                id="smCampaignName"
                value={formData.campaignName}
                onChange={e => handleInputChange('campaignName', e.target.value)}
                placeholder="e.g. October Hair Colour Flash Sale"
                maxLength={150}
                className={fieldErrors.campaignName ? 'input-error' : ''}
              />
              {fieldErrors.campaignName && <span className="field-error-message">{fieldErrors.campaignName}</span>}
              <div className="form-helper-text">
                Internal reference name for tracking this campaign's conversions.
              </div>
            </div>
          </div>

          {/* 2. Messages */}
          <div className="form-section">
            <h4>2. Message(s)</h4>
            <p className="form-helper-text" style={{ marginBottom: 12 }}>
              Enter the post copy you plan to publish on Meta / Instagram / TikTok. Adding the exact text here helps track which message drove bookings.
            </p>
            {formData.messages.map((msg, idx) => (
              <div key={idx} className="form-group" style={{ marginBottom: 12 }}>
                <label>Message {formData.messages.length > 1 ? idx + 1 : ''}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <textarea
                    value={msg}
                    onChange={e => handleMessageChange(idx, e.target.value)}
                    rows={4}
                    placeholder="Write your social media post copy here…"
                    maxLength={2200}
                    style={{ flex: 1 }}
                    className={idx === 0 && fieldErrors.messages ? 'input-error' : ''}
                  />
                  {formData.messages.length > 1 && (
                    <button
                      type="button"
                      className="template-delete-button"
                      style={{ flexShrink: 0, alignSelf: 'flex-start' }}
                      onClick={() => removeMessage(idx)}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="form-helper-text">{msg.length} / 2200 characters</div>
              </div>
            ))}
            {fieldErrors.messages && <span className="field-error-message">{fieldErrors.messages}</span>}
            <button type="button" className="preview-exclusion-button" onClick={addMessage} style={{ maxWidth: 220 }}>
              <span className="button-icon">＋</span>
              <div className="button-content"><span className="button-title">Add Another Message</span></div>
            </button>
          </div>

          {/* 3. Scheduling */}
          <div className="form-section">
            <h4>3. Scheduling</h4>

            <div className="form-group">
              <label>Scheduling Type</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    value="one-time"
                    checked={formData.schedulingType === 'one-time'}
                    onChange={() => handleInputChange('schedulingType', 'one-time')}
                  />
                  One-time
                </label>
                <label className="radio-label">
                  <input
                    type="radio"
                    value="recurring"
                    checked={formData.schedulingType === 'recurring'}
                    onChange={() => handleInputChange('schedulingType', 'recurring')}
                  />
                  Recurring
                </label>
              </div>
            </div>

            {formData.schedulingType === 'one-time' && (
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="smScheduledDate">
                    Campaign Date<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    id="smScheduledDate"
                    value={formData.scheduledDate}
                    onChange={e => handleInputChange('scheduledDate', e.target.value)}
                    className={fieldErrors.scheduledDate ? 'input-error' : ''}
                  />
                  {fieldErrors.scheduledDate && <span className="field-error-message">{fieldErrors.scheduledDate}</span>}
                  {fieldErrors.scheduledDateTime && <span className="field-error-message">{fieldErrors.scheduledDateTime}</span>}
                </div>
                <div className="form-group">
                  <label htmlFor="smScheduledTime">
                    Campaign Time<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="time"
                    id="smScheduledTime"
                    value={formData.scheduledTime}
                    onChange={e => handleInputChange('scheduledTime', e.target.value)}
                    className={fieldErrors.scheduledTime ? 'input-error' : ''}
                  />
                  {fieldErrors.scheduledTime && <span className="field-error-message">{fieldErrors.scheduledTime}</span>}
                </div>
              </div>
            )}

            {formData.schedulingType === 'recurring' && (
              <>
                <div className="form-group">
                  <label htmlFor="smRecurringTime">
                    Campaign Time<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="time"
                    id="smRecurringTime"
                    value={formData.scheduledTime}
                    onChange={e => handleInputChange('scheduledTime', e.target.value)}
                    className={fieldErrors.scheduledTime ? 'input-error' : ''}
                  />
                  {fieldErrors.scheduledTime && <span className="field-error-message">{fieldErrors.scheduledTime}</span>}
                </div>

                <div className="form-group">
                  <label>Frequency</label>
                  <select
                    value={formData.recurring.frequency}
                    onChange={e => handleRecurringChange('frequency', e.target.value)}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="smStartDate">
                      Start Date<span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      id="smStartDate"
                      value={formData.recurring.startDate}
                      onChange={e => handleRecurringChange('startDate', e.target.value)}
                      className={fieldErrors.recurringStartDate ? 'input-error' : ''}
                    />
                    {fieldErrors.recurringStartDate && <span className="field-error-message">{fieldErrors.recurringStartDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>End Type</label>
                    <select
                      value={formData.recurring.endType}
                      onChange={e => handleRecurringChange('endType', e.target.value)}
                    >
                      <option value="date">End by Date</option>
                      <option value="cycles">End after Cycles</option>
                    </select>
                  </div>
                </div>

                {formData.recurring.endType === 'date' && (
                  <div className="form-group">
                    <label htmlFor="smEndDate">
                      End Date<span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="date"
                      id="smEndDate"
                      value={formData.recurring.endDate}
                      onChange={e => handleRecurringChange('endDate', e.target.value)}
                      className={fieldErrors.recurringEndDate ? 'input-error' : ''}
                    />
                    {fieldErrors.recurringEndDate && <span className="field-error-message">{fieldErrors.recurringEndDate}</span>}
                  </div>
                )}

                {formData.recurring.endType === 'cycles' && (
                  <div className="form-group">
                    <label>Number of Cycles<span className="required-asterisk">*</span></label>
                    <input
                      type="number"
                      min="1"
                      value={formData.recurring.numberOfCycles}
                      onChange={e => handleRecurringChange('numberOfCycles', parseInt(e.target.value) || 0)}
                      className={fieldErrors.numberOfCycles ? 'input-error' : ''}
                    />
                    {fieldErrors.numberOfCycles && <span className="field-error-message">{fieldErrors.numberOfCycles}</span>}
                  </div>
                )}
              </>
            )}
          </div>

          {/* 4. Conversion Tracking */}
          <div className="form-section">
            <h4>4. Conversion Tracking</h4>
            <div className="form-group">
              <label htmlFor="smConversionPeriod">
                Conversion Window (days)
              </label>
              <input
                type="number"
                id="smConversionPeriod"
                min="1"
                max="365"
                value={formData.conversionPeriod}
                onChange={e => handleInputChange('conversionPeriod', parseInt(e.target.value) || 30)}
              />
              <div className="form-helper-text">
                Bookings made within this period after the campaign date will be attributed to this campaign as conversions.
              </div>
            </div>
          </div>

          {/* 5. Customer Targeting */}
          <div className="form-section">
            <h4>5. Customer Targeting</h4>
            <p className="form-helper-text">
              Select the customer segment you are targeting with this social media campaign. Only customers who have opted in to Social Media Campaigns will be included in conversion tracking.
            </p>

            <div className="form-group">
              <label htmlFor="smTargetingType">
                Target Audience<span className="required-asterisk">*</span>
              </label>
              <select
                id="smTargetingType"
                value={formData.targetingType}
                onChange={e => handleInputChange('targetingType', e.target.value)}
                className={fieldErrors.targetingType ? 'input-error' : ''}
              >
                <option value="not_selected">Select Target Audience</option>
                <option value="all_customers">All Customers</option>
                <option value="customer_profile">Customer Profile</option>
                <option value="customer_transaction">Customer Transaction History</option>
                <option value="customer_points">Loyalty Points &amp; Tier</option>
                <option value="customer_appointment">Appointment-Based</option>
                <option value="customer_notes">Customer Notes</option>
                <option value="specific_customer">Specific Customers</option>
              </select>
              {fieldErrors.targetingType && <span className="field-error-message">{fieldErrors.targetingType}</span>}
            </div>

            {/* Customer Profile Criteria */}
            {formData.targetingType === 'customer_profile' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Birth Months (hold Ctrl/Cmd to multi-select)</label>
                  <select
                    multiple
                    value={formData.targetingCriteria.birthMonths}
                    onChange={handleBirthMonthsChange}
                    size={4}
                  >
                    {BIRTH_MONTH_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Postal Codes (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.targetingCriteria.postalCodes.join(', ')}
                    onChange={handlePostalCodesChange}
                    placeholder="e.g. 123, 456, 789"
                  />
                </div>
              </div>
            )}

            {/* Transaction History Criteria */}
            {formData.targetingType === 'customer_transaction' && (
              <div className="targeting-criteria">
                {isMultiLocation && (
                  <div className="form-group">
                    <label>Location</label>
                    <select
                      value={formData.targetingCriteria.transactionLocationId}
                      onChange={e => handleTargetingCriteriaChange('transactionLocationId', e.target.value)}
                    >
                      <option value="not_selected">All Locations</option>
                      {locations.map(loc => (
                        <option key={loc.locationId} value={loc.locationId}>{loc.locationName}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Number of Visits</label>
                    <select
                      value={formData.targetingCriteria.transactionType}
                      onChange={e => handleTargetingCriteriaChange('transactionType', e.target.value)}
                    >
                      <option value="not_selected">No filter</option>
                      <option value="less_than">Less than</option>
                      <option value="equal_to">Equal to</option>
                      <option value="greater_than">Greater than</option>
                    </select>
                  </div>
                  {formData.targetingCriteria.transactionType !== 'not_selected' && (
                    <div className="form-group">
                      <label>Visit Count</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.targetingCriteria.transactionCount}
                        onChange={e => handleTargetingCriteriaChange('transactionCount', parseInt(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Total Spent</label>
                    <select
                      value={formData.targetingCriteria.transactionAmountType}
                      onChange={e => handleTargetingCriteriaChange('transactionAmountType', e.target.value)}
                    >
                      <option value="not_selected">No filter</option>
                      <option value="less_than">Less than</option>
                      <option value="greater_than">Greater than</option>
                    </select>
                  </div>
                  {formData.targetingCriteria.transactionAmountType !== 'not_selected' && (
                    <div className="form-group">
                      <label>Amount ($)</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.targetingCriteria.transactionAmount}
                        onChange={e => handleTargetingCriteriaChange('transactionAmount', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Last Transaction Within</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.targetingCriteria.lastTransactionMonths}
                      onChange={e => handleTargetingCriteriaChange('lastTransactionMonths', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <select
                      value={formData.targetingCriteria.lastTransactionUnit}
                      onChange={e => handleTargetingCriteriaChange('lastTransactionUnit', e.target.value)}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Loyalty Points & Tier Criteria */}
            {formData.targetingType === 'customer_points' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Member Tiers (hold Ctrl/Cmd to multi-select)</label>
                  <select
                    multiple
                    value={formData.targetingCriteria.memberTiers}
                    onChange={handleMemberTiersChange}
                    size={4}
                  >
                    {MEMBER_TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Points Balance</label>
                    <select
                      value={formData.targetingCriteria.pointsType}
                      onChange={e => handleTargetingCriteriaChange('pointsType', e.target.value)}
                    >
                      <option value="not_selected">No filter</option>
                      <option value="less_than">Less than</option>
                      <option value="greater_than">Greater than</option>
                    </select>
                  </div>
                  {formData.targetingCriteria.pointsType !== 'not_selected' && (
                    <div className="form-group">
                      <label>Points</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.targetingCriteria.pointsAmount}
                        onChange={e => handleTargetingCriteriaChange('pointsAmount', parseInt(e.target.value) || 0)}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Appointment-based Criteria */}
            {formData.targetingType === 'customer_appointment' && (
              <div className="targeting-criteria">
                <div className="form-row">
                  <div className="form-group">
                    <label>Appointment within next</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.targetingCriteria.appointmentTimeValue}
                      onChange={e => handleTargetingCriteriaChange('appointmentTimeValue', parseInt(e.target.value) || 0)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <select
                      value={formData.targetingCriteria.appointmentTimeUnit}
                      onChange={e => handleTargetingCriteriaChange('appointmentTimeUnit', e.target.value)}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Service</label>
                  <select
                    value={formData.targetingCriteria.appointmentService}
                    onChange={e => handleTargetingCriteriaChange('appointmentService', e.target.value)}
                  >
                    <option value="not_selected">All Services</option>
                    {salonServices.map(s => <option key={s.id} value={s.id}>{s.name || s.serviceName}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Staff</label>
                  <select
                    value={formData.targetingCriteria.appointmentStaff}
                    onChange={e => handleTargetingCriteriaChange('appointmentStaff', e.target.value)}
                  >
                    <option value="not_selected">All Staff</option>
                    {salonStaff.map(s => <option key={s.id} value={s.id}>{s.name || s.displayName}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Customer Notes Criteria */}
            {formData.targetingType === 'customer_notes' && (
              <div className="targeting-criteria">
                <div className="form-group">
                  <label>Search Text in Notes</label>
                  <input
                    type="text"
                    value={formData.targetingCriteria.notesSearchText}
                    onChange={e => handleTargetingCriteriaChange('notesSearchText', e.target.value)}
                    placeholder="e.g. colour treatment, sensitive scalp"
                  />
                </div>
                <div className="form-group">
                  <label>Match Type</label>
                  <select
                    value={formData.targetingCriteria.notesMatchType}
                    onChange={e => handleTargetingCriteriaChange('notesMatchType', e.target.value)}
                  >
                    <option value="contains_all">Contains All Words</option>
                    <option value="contains_any">Contains Any Word</option>
                    <option value="exact">Exact Match</option>
                  </select>
                </div>
              </div>
            )}

            {/* Specific Customer Search */}
            {formData.targetingType === 'specific_customer' && (
              <div className="targeting-criteria">
                <div className="form-group customer-search-container">
                  <label>Search &amp; Select Customers</label>
                  <input
                    type="text"
                    value={customerSearchQuery}
                    onChange={handleCustomerSearchChange}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search by name or email…"
                  />
                  {searchingCustomers && <div className="search-loading">Searching…</div>}
                  {showCustomerDropdown && customerSearchResults.length > 0 && (
                    <div className="customer-dropdown">
                      {customerSearchResults.map(c => (
                        <div key={c.id} className="customer-option" onClick={() => handleCustomerSelect(c)}>
                          <span className="customer-name">{c.displayName || 'Unknown'}</span>
                          <span className="customer-email">{c.email || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedCustomers.length > 0 && (
                    <div className="selected-customers-chips">
                      {selectedCustomers.map(c => (
                        <div key={c.id} className="customer-chip">
                          <span>{c.displayName || c.email}</span>
                          <button type="button" onClick={() => handleCustomerRemove(c.id)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {fieldErrors.specificCustomers && <span className="field-error-message">{fieldErrors.specificCustomers}</span>}
                </div>
              </div>
            )}

            {formData.targetingType !== 'not_selected' && (
              <button
                type="button"
                className="save-targeting-button"
                onClick={handleSaveTargetingCriteria}
                style={{ marginTop: 12 }}
              >
                {targetingCriteriaSaved ? '✓ Targeting Criteria Saved' : 'Save Targeting Criteria'}
              </button>
            )}
          </div>

          {/* 6. Final Customers Targeted */}
          <FinalCustomersTargeted
            salonId={salonId}
            campaignType="social_media"
            formData={formData}
            targetingCriteriaSaved={targetingCriteriaSaved}
            disabled={!targetingCriteriaSaved}
          />

          {/* Schedule Button */}
          <div className="form-section" style={{ marginTop: 24 }}>
            <div className="form-actions" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="send-button"
                onClick={handleSchedule}
                disabled={saving}
                style={{ minWidth: 220 }}
              >
                {saving ? 'Recording…' : 'Schedule Campaign'}
              </button>
            </div>
            <div className="form-helper-text" style={{ marginTop: 10 }}>
              <strong>Note:</strong> Clicking Schedule does not publish or send anything. It records this campaign in BespokeBeauty and starts conversion tracking for the segment above.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SocialMediaCampaignsEditor;
